import React, { useState, useMemo } from "react";
import { Table, DatePicker, Input } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import { ClipboardList, Search } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetInventoryReportSoldItems } from "../../../Api";
import { DEBOUNCED_SEARCH_QUERY } from "../../../Constant";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

const { RangePicker } = DatePicker;

export const SoldItemsReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);

  const startDateStr = dateRange[0].format("YYYY-MM-DD");
  const endDateStr = dateRange[1].format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["sold-items-report", startDateStr, endDateStr, search],
    queryFn: () =>
      GetInventoryReportSoldItems({
        start_date: startDateStr,
        end_date: endDateStr,
        search,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const qty = reportData.reduce(
      (sum: number, item: any) => sum + (item.quantity || 0),
      0,
    );
    const disc = reportData.reduce(
      (sum: number, item: any) => sum + (item.discount_amount || 0),
      0,
    );
    const sales = reportData.reduce(
      (sum: number, item: any) => sum + (item.total_price || 0),
      0,
    );
    return [
      {
        title: "Items Sold Volume",
        value: `${qty} Units`,
        colorClass: "text-indigo-655",
      },
      {
        title: "Sales Discount Given",
        value: formatLKR(disc),
        colorClass: "text-rose-500",
      },
      {
        title: "Net Revenue Collections",
        value: formatLKR(sales),
        colorClass: "text-cyan-700 font-black",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Sale Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (v: string) => (
        <span className="text-xs text-gray-500">
          {dayjs(v).format("YYYY-MM-DD HH:mm")}
        </span>
      ),
    },
    {
      title: "Order No",
      dataIndex: "order_no",
      key: "order_no",
      render: (v: string) => (
        <span className="font-bold text-xs text-gray-800">#{v}</span>
      ),
    },
    {
      title: "Product Sold",
      dataIndex: "product_title",
      key: "product_title",
      render: (v: string, record: any) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-bold text-gray-855">{v}</span>
          <span className="text-[10px] text-gray-400">
            SKU: {record.product_sku} | Cat: {record.category_name}
          </span>
        </div>
      ),
    },
    {
      title: "Qty Sold",
      dataIndex: "quantity",
      key: "quantity",
      align: "center" as const,
      render: (v: number) => (
        <span className="text-xs font-bold text-gray-800">{v}</span>
      ),
    },
    {
      title: "Unit Price",
      dataIndex: "price",
      key: "price",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs text-gray-550 font-semibold">
          LKR {v.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Sold Price",
      dataIndex: "sold_price",
      key: "sold_price",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs text-gray-550 font-semibold">
          {v > 0 ? `LKR ${v.toFixed(2)}` : "—"}
        </span>
      ),
    },
    {
      title: "Discount Given",
      dataIndex: "discount_amount",
      key: "discount_amount",
      align: "right" as const,
      render: (v: number) => (
        <span
          className={`text-xs font-medium ${v > 0 ? "text-rose-500" : "text-gray-300"}`}
        >
          {v > 0 ? `-LKR ${v.toFixed(2)}` : "—"}
        </span>
      ),
    },
    {
      title: "Subtotal Value",
      dataIndex: "total_price",
      key: "total_price",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-extrabold text-gray-955">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Reporting Date Period
        </span>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            dates && dates[0] && dates[1] && setDateRange([dates[0], dates[1]])
          }
          allowClear={false}
          className="h-9 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
        />
      </div>

      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Search Records
        </span>
        <Input
          placeholder="Search order no, product sku..."
          prefix={<Search className="w-3.5 h-3.5 text-gray-450 mr-1.5" />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          allowClear
          className="w-72 h-9 rounded-lg text-xs"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Sold Items Report"
      description="Review granular logs of sold items, unit prices, discounts applied, and totals per transaction."
      icon={<ClipboardList className="w-6 h-6 text-cyan-600" />}
      backPath="/system/inventory-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "sold_items_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) => record.id || Math.random().toString()}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
